import React, { useState, useEffect } from 'react';
import { Globe2 } from 'lucide-react';
import { authAxios } from '@/lib/api';
import CountryHighlights from './CountryHighlights';
import CountryChart from './CountryChart';
import CountryTable from './CountryTable';

const PERIODS = [
  { days: 30, label: '30d' },
  { days: 90, label: '90d' },
  { days: 365, label: '1y' },
  { days: 0, label: 'All time' },
];

function PeriodPills({ value, onChange }) {
  return (
    <div className="flex items-center gap-1.5">
      {PERIODS.map(p => (
        <button key={p.days} onClick={() => onChange(p.days)} className={`text-xs font-sans px-3 py-1.5 rounded-md transition-colors ${value === p.days ? 'bg-saffron text-white font-semibold' : 'bg-white text-earth/50 border border-earth/15 hover:border-saffron/30'}`} data-testid={`country-period-${p.days}`}>
          {p.label}
        </button>
      ))}
    </div>
  );
}

export default function CountryInsights() {
  const [days, setDays] = useState(0);
  const [sortKey, setSortKey] = useState('inquiries');
  const [data, setData] = useState(null);
  const [status, setStatus] = useState('loading');

  useEffect(() => {
    let cancelled = false;
    setStatus('loading');
    authAxios.get(`/api/analytics/countries?days=${days}`)
      .then(r => { if (!cancelled) { setData(r.data); setStatus('ready'); } })
      .catch(() => { if (!cancelled) setStatus('error'); });
    return () => { cancelled = true; };
  }, [days]);

  return (
    <section className="space-y-4" data-testid="country-insights">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="font-display text-xl font-bold text-earth-dark flex items-center gap-2"><Globe2 size={18} className="text-saffron" /> Buyer Country Insights</h2>
          <p className="text-xs text-earth/50 font-sans mt-0.5">Where your inquiries and won orders come from. Use it to decide which markets to focus on.</p>
        </div>
        <PeriodPills value={days} onChange={setDays} />
      </div>

      {status === 'loading' && <p className="text-center py-10 text-earth/40 font-sans text-sm animate-pulse" data-testid="country-insights-loading">Loading country insights...</p>}
      {status === 'error' && <p className="text-center py-10 text-chilli font-sans text-sm" data-testid="country-insights-error">Could not load country insights</p>}
      {status === 'ready' && data.total_inquiries === 0 && (
        <p className="text-center py-10 bg-white rounded-lg border border-saffron-100/50 text-earth/40 font-sans text-sm" data-testid="country-insights-empty">No inquiries in this period yet.</p>
      )}
      {status === 'ready' && data.total_inquiries > 0 && (
        <>
          <CountryHighlights data={data} />
          <CountryChart countries={data.countries} />
          <CountryTable countries={data.countries} sortKey={sortKey} onSort={setSortKey} />
        </>
      )}
    </section>
  );
}
